// ─────────────────────────────────────────────────────────────────────────────
// Agrégats du tableau santé d'équipe — fonctions pures (testables, sans
// Firestore). Compile les sondages individuels mensuels par membre et par mois,
// et mesure l'écart entre le ressenti de l'employé (workload_score du sondage)
// et la perception du gestionnaire (note `workload` du check-in trimestriel).
// Les deux échelles vont de 1 (à risque) à 5 (confortable).
// ─────────────────────────────────────────────────────────────────────────────

import type { EmployeeCheckin, MemberPulse } from '../types/firestore'

export type MemberHealthRow = {
  email: string
  // Sondages du membre, chronologiques (period croissante).
  pulses: MemberPulse[]
  latest: MemberPulse | null
  // Moyennes des 3 derniers sondages (≈ 3 mois), 1 décimale.
  avg3Workload: number | null
  avg3Energy: number | null
  // Perception gestionnaire : note `workload` du check-in le plus récent.
  managerWorkload: number | null
  // Écart employé − gestionnaire (négatif ⇒ l'employé se sent plus chargé
  // que ce que le gestionnaire perçoit). null si l'un des deux manque.
  workloadGap: number | null
}

export type TeamMonthlyAverage = {
  period: string
  workload: number | null
  energy: number | null
  count: number
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

function avg(values: number[]): number | null {
  if (values.length === 0) return null
  return round1(values.reduce((s, v) => s + v, 0) / values.length)
}

// Note `workload` du check-in le plus récent (toutes années). null si aucun
// check-in ne contient cette question (anciens check-ins à 5 questions).
export function latestManagerWorkload(
  checkins: EmployeeCheckin[],
): number | null {
  const ordered = [...checkins].sort(
    (a, b) => b.year - a.year || b.quarter - a.quarter,
  )
  for (const c of ordered) {
    const answer = c.answers.find((a) => a.key === 'workload')
    if (answer && answer.rating > 0) return answer.rating
  }
  return null
}

export function buildMemberHealthRow(
  email: string,
  pulses: MemberPulse[],
  checkins: EmployeeCheckin[],
): MemberHealthRow {
  const chrono = [...pulses].sort((a, b) => a.period.localeCompare(b.period))
  const last3 = chrono.slice(-3)
  const avg3Workload = avg(last3.map((p) => p.workload_score))
  const managerWorkload = latestManagerWorkload(checkins)
  return {
    email,
    pulses: chrono,
    latest: chrono.at(-1) ?? null,
    avg3Workload,
    avg3Energy: avg(last3.map((p) => p.energy_score)),
    managerWorkload,
    workloadGap:
      avg3Workload !== null && managerWorkload !== null
        ? round1(avg3Workload - managerWorkload)
        : null,
  }
}

// Moyennes d'équipe par mois (periods croissantes), sur les périodes présentes.
export function teamMonthlyAverages(pulses: MemberPulse[]): TeamMonthlyAverage[] {
  const byPeriod = new Map<string, MemberPulse[]>()
  for (const p of pulses) {
    const list = byPeriod.get(p.period) ?? []
    list.push(p)
    byPeriod.set(p.period, list)
  }
  return [...byPeriod.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, list]) => ({
      period,
      workload: avg(list.map((p) => p.workload_score)),
      energy: avg(list.map((p) => p.energy_score)),
      count: list.length,
    }))
}
